import { useEffect, useState } from "react";
import { ClipboardList, ChevronDown, Star, ShieldCheck, ShieldAlert, Printer } from "lucide-react";
import { Link } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";

const EVT = "cuestionario-claves";
const useCuestionarios = (patientId) => {
  const [list, setList] = useState([]);
  useEffect(() => {
    const load = () => api.get(`/patients/${patientId}/cuestionarios`).then((r) => setList(r.data));
    load();
    window.addEventListener(EVT, load);
    return () => window.removeEventListener(EVT, load);
  }, [patientId]);
  return list;
};

const Cuestionario = ({ c }) => {
  const [open, setOpen] = useState(false);
  const [claves, setClaves] = useState(c.claves || []);
  const toggle = async (i) => {
    const nuevas = claves.includes(i) ? claves.filter((x) => x !== i) : [...claves, i];
    setClaves(nuevas);
    await api.put(`/cuestionarios/${c.id}/claves`, { claves: nuevas });
    window.dispatchEvent(new Event(EVT));
  };
  const secciones = c.respuestas.reduce((acc, r, i) => {
    (acc[r.seccion || "General"] ||= []).push({ ...r, i });
    return acc;
  }, {});
  return (
    <div className="border-t border-slate-100 py-2">
      <button onClick={() => setOpen(!open)} data-testid={`cuestionario-${c.id}`} className="w-full flex items-center gap-2 text-left text-sm">
        <span className="flex-1 font-medium text-slate-800">{c.titulo || c.codigo}</span>
        <span className="text-xs text-slate-500">{fmtDate(c.fecha)}</span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div className="flex flex-wrap items-center gap-2 mt-1">
        {c.proteccion?.acepta
          ? <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] text-emerald-800" data-testid={`proteccion-ok-${c.id}`}><ShieldCheck className="h-3.5 w-3.5" />Protección de datos aceptada {fmtDate(c.proteccion.fecha)}</span>
          : <span className="inline-flex items-center gap-1 rounded bg-rose-50 px-1.5 py-0.5 text-[11px] text-rose-700" data-testid={`proteccion-no-${c.id}`}><ShieldAlert className="h-3.5 w-3.5" />Sin aceptación de protección de datos</span>}
        <Link to={`/cuestionarios/${c.id}/imprimir`} target="_blank" data-testid={`cuestionario-imprimir-${c.id}`} className="inline-flex items-center gap-1 text-[11px] text-[#1E3A8A] hover:underline"><Printer className="h-3.5 w-3.5" />Ver completo / PDF</Link>
      </div>
      {open && <p className="text-[11px] text-slate-400 mt-1">Marca con la estrella las respuestas clave para verlas en la evaluación.</p>}
      {open && Object.entries(secciones).map(([s, rs]) => (
        <div key={s} className="mt-2">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{s}</div>
          {rs.map((r) => (
            <div key={r.i} className="flex items-start gap-1 text-xs mt-1">
              <button type="button" onClick={() => toggle(r.i)} data-testid={`clave-${c.id}-${r.i}`} title="Respuesta clave">
                <Star className={`h-3.5 w-3.5 ${claves.includes(r.i) ? "fill-amber-400 text-amber-500" : "text-slate-300"}`} />
              </button>
              <span><span className="text-slate-500">{r.pregunta}</span> <span className="text-slate-900 font-medium">{r.respuesta || "—"}</span></span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export const CuestionariosPaciente = ({ patientId }) => {
  const list = useCuestionarios(patientId);
  if (!list.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="cuestionarios-paciente">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1"><ClipboardList className="h-4 w-4" />Cuestionarios de la web</div>
      {list.map((c) => <Cuestionario key={c.id} c={c} />)}
    </div>
  );
};

export const ClavesCuestionario = ({ patientId, motivo }) => {
  const list = useCuestionarios(patientId);
  if (!list.length && !motivo) return null;
  const claves = list.flatMap((c) => (c.claves || []).map((i) => ({ ...c.respuestas[i], k: `${c.id}-${i}` })).filter((r) => r.pregunta));
  return (
    <div className="mb-4 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2" data-testid="claves-cuestionario">
      <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-amber-800"><Star className="h-3.5 w-3.5" />Para el informe: lo que cuenta la familia</div>
      {motivo && <div className="text-xs mt-1"><span className="text-slate-500">Motivo de consulta:</span> <span className="font-medium text-slate-900">{motivo}</span></div>}
      {claves.map((r) => <div key={r.k} className="text-xs mt-0.5"><span className="text-slate-500">{r.pregunta}</span> <span className="font-medium text-slate-900">{r.respuesta}</span></div>)}
      {list.length > 0 && !claves.length && <div className="text-[11px] text-slate-500 mt-1">Marca con la estrella las respuestas importantes en "Cuestionarios de la web" (a la derecha) y aparecerán aquí.</div>}
    </div>
  );
};
